import React from 'react'
import styled from 'styled-components'
import { propOr } from 'ramda'
import DeleteStudentModal from 'modules/students/components/DeleteStudentModal'
import EditStudentModal from 'modules/students/components/EditStudentModal'
import MessageForStudentButtons from 'modules/newStudents/components/MessageForStudentButtons'
import MessageForTeacherModal from 'modules/newStudents/components/MessageForTeacherModal'
import MarkStudentAsReadyModal from 'modules/newStudents/components/MarkStudentAsReadyModal'
import AssignTeachersModal from 'modules/students/components/AssignTeachersModal'
import SlotsEditorInline from 'modules/newStudents/components/SlotsEditorInline'
import { redirect } from 'utils/navigation'
import { isNotNilOrEmpty } from 'utils/ramda'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { copyToClipboard } from 'utils/date'
import { Tooltip } from '@mui/material'
import { updateStudent } from 'services/StudentsService'
import { removeTeacherEntriesFromAdditionalData } from 'modules/newStudents/utils/additionalData'

const NewStudentsTableRow = ({ student, actionCallback, schoolSubjects }) => {
  const studentTeachers = propOr([], 'teachers', student)
  const toBeDeleted = propOr(false, 'toBeDeleted', student)
  const toBeDeletedToggledBy = propOr('teacher', 'toBeDeletedToggledBy', student)

  const plannedRaw = student.plannedSubjects
  let plannedCellContent = null
  if (typeof plannedRaw === 'string' && plannedRaw.trim()) {
    plannedCellContent = plannedRaw.trim()
  } else if (Array.isArray(plannedRaw) && plannedRaw.length > 0) {
    const labels = plannedRaw
      .map(id => schoolSubjects.find(s => s.id === id)?.name)
      .filter(Boolean)
    plannedCellContent = labels.length > 0 ? labels.join(', ') : null
  }

  const subjects = studentTeachers.map(t => t.subject || '—')

  const handleRemoveTeacherAssignment = (teacherRow, index) => {
    const newTeachers = studentTeachers.filter((_, i) => i !== index)
    const additionalData = removeTeacherEntriesFromAdditionalData(student, teacherRow, schoolSubjects)
    updateStudent({
      student: {
        ...student,
        teachers: newTeachers,
        additionalData
      },
      callback: typeof actionCallback === 'function' ? actionCallback : undefined
    })
  }

  return (
    <Wrapper
      toBeDeleted={toBeDeleted}
      toBeDeletedToggledBy={toBeDeletedToggledBy}
      onClick={() => redirect(`/students/${student.id}`)}
    >
      <td>
        <StudentName>
          {student.name}
          <Tooltip title='Skopiuj nazwę'>
            <CopyIcon onClick={e => {
              e.stopPropagation()
              copyToClipboard(student.name.trim())()
            }} />
          </Tooltip>
        </StudentName>
      </td>
      <PlannedSubjectsCell>
        {plannedCellContent || '—'}
      </PlannedSubjectsCell>
      <StackedCell>
        {subjects.length > 0 ? subjects.map((s, i) => <StackedLine key={i}>{s}</StackedLine>) : '—'}
      </StackedCell>
      <TeacherNamesCell onClick={e => e.stopPropagation()}>
        {studentTeachers.length > 0
          ? studentTeachers.map((teacherRow, i) => (
            <TeacherNameLine key={teacherRow.entryId || `${teacherRow.id}-${i}`}>
              <span>{teacherRow.name || '—'}</span>
              <Tooltip title='Usuń przypisanie nauczyciela'>
                <RemoveTeacherIcon
                  fontSize='inherit'
                  onClick={e => {
                    e.stopPropagation()
                    handleRemoveTeacherAssignment(teacherRow, i)
                  }}
                />
              </Tooltip>
            </TeacherNameLine>
          ))
          : '—'}
      </TeacherNamesCell>
      <DayTimeCell>
        {studentTeachers.length > 0 ? (
          <TeachersSlotsStack>
            {studentTeachers.map((teacher, i) => (
              <React.Fragment key={teacher.entryId || `${teacher.id}-${i}`}>
                {i > 0 && <BlockSeparator />}
                <TeacherBlock>
                  {studentTeachers.length > 1 && <TeacherLabel>{teacher.name}</TeacherLabel>}
                  <SlotsEditorInline
                    student={student}
                    teacher={teacher}
                    schoolSubjects={schoolSubjects}
                    actionCallback={actionCallback}
                    mode='both'
                  />
                </TeacherBlock>
              </React.Fragment>
            ))}
          </TeachersSlotsStack>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )}
      </DayTimeCell>
      <td>{isNotNilOrEmpty(student.comment) ? '✔' : '✖'}</td>
      <td>
        <Actions onClick={e => e.stopPropagation()}>
          <AssignTeachersModal student={student} actionCallback={actionCallback} showAccountSection />
          <MessageForStudentButtons student={student} />
          <MessageForTeacherModal student={student} />
          <MarkStudentAsReadyModal student={student} actionCallback={actionCallback} />
          <EditStudentModal student={student} isTeacherView actionCallback={actionCallback} />
          <DeleteStudentModal student={student} actionCallback={actionCallback} />
        </Actions>
      </td>
    </Wrapper>
  )
}

export default NewStudentsTableRow

const Wrapper = styled.tr`
  font-size: 13px;
  color: ${({ toBeDeleted, toBeDeletedToggledBy, theme }) => toBeDeleted
    ? toBeDeletedToggledBy === 'teacher'
      ? '#27AEB9'
      : theme.colors.error
    : 'default'};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey};
    cursor: pointer;
  }
`

const StudentName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const CopyIcon = styled(ContentCopyIcon)`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 16px !important;
  padding: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary.main};
  }
`

const DayTimeCell = styled.td`
  vertical-align: middle;
`

const TeachersSlotsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
`

const TeacherBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 2px;
`

const TeacherLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const PlannedSubjectsCell = styled.td`
  vertical-align: middle;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 220px;
`

const StackedCell = styled.td`
  vertical-align: middle;
  text-align: center;
`

const TeacherNamesCell = styled.td`
  vertical-align: middle;
  text-align: left;
`

const StackedLine = styled.div`
  line-height: 1.4;
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`

const TeacherNameLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  line-height: 1.4;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`

const RemoveTeacherIcon = styled(DeleteOutlineIcon)`
  font-size: 15px !important;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.darkGrey};
  cursor: pointer;
  padding: 1px;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`

const BlockSeparator = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px dashed ${({ theme }) => theme.colors.border || '#ccc'};
  margin: 6px 0;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
`
